import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import type { LoggedInUser, User, UserActivityResponse } from '../types/user';

// ===== QUERY KEYS =====
export const userKeys = {
  all: ['users'] as const,
  list: (facilityId: number) => [...userKeys.all, 'list', facilityId] as const,
  detail: (facilityId: number, userId: number) =>
    [...userKeys.all, 'detail', facilityId, userId] as const,
  deleted: (facilityId: number) => [...userKeys.all, 'deleted', facilityId] as const,
  loggedIn: (facilityId: number) => [...userKeys.all, 'loggedIn', facilityId] as const,
  signature: (facilityId: number, userId: number) =>
    [...userKeys.all, 'signature', facilityId, userId] as const,
  activity: (facilityId: number, userId: number, from: string, to: string) =>
    [...userKeys.all, 'activity', facilityId, userId, from, to] as const,
  myDefaultFacility: (userId: number) => [...userKeys.all, 'me', 'defaultFacility', userId] as const,
  myStartForm: (userId: number) => [...userKeys.all, 'me', 'startForm', userId] as const,
};

// ===== QUERIES =====

export function useUsers(facilityId: number) {
  return useQuery({
    queryKey: userKeys.list(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<User[]>(API.users(facilityId));
      return data;
    },
    enabled: facilityId > 0,
    staleTime: 30 * 1000,
  });
}

export function useUser(facilityId: number, userId: number) {
  return useQuery({
    queryKey: userKeys.detail(facilityId, userId),
    queryFn: async () => {
      const { data } = await apiClient.get<User>(API.userById(facilityId, userId));
      return data;
    },
    enabled: facilityId > 0 && userId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useDeletedUsers(facilityId: number) {
  return useQuery({
    queryKey: userKeys.deleted(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<User[]>(API.userDeleted(facilityId));
      return data;
    },
    enabled: facilityId > 0,
    staleTime: 30 * 1000,
  });
}

/**
 * Who is signed in, as the API scopes it for the viewer (Users Logged In right or PSI staff; customers see
 * the users of their own facilities only).
 */
export function useLoggedInUsers(facilityId: number) {
  return useQuery({
    queryKey: userKeys.loggedIn(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<LoggedInUser[]>(API.userLoggedIn(facilityId));
      return data;
    },
    enabled: facilityId > 0,
    staleTime: 30 * 1000,
  });
}

// ===== MUTATIONS =====

export function useSaveUser(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (user: Partial<User>) => {
      const { data } = await apiClient.post<number>(API.userAddMod(facilityId), user);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.list(facilityId) });
      useNotificationStore.getState().addNotification('User saved', 'success');
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to save user', 'error');
    },
  });
}

/**
 * The facility the signed-in user opens to (tblPMSec_User.DF_Dietech_Facility),
 * read from /Me so it is always the caller's own row. 0 = none set.
 */
export function useMyDefaultFacility(userId: number, enabled = true) {
  return useQuery({
    queryKey: userKeys.myDefaultFacility(userId),
    queryFn: async () => {
      const { data } = await apiClient.get<{ dF_Dietech_Facility?: number | null }>(API.userMe);
      return Number(data?.dF_Dietech_Facility ?? 0);
    },
    enabled: enabled && userId > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * Sets the signed-in user's default facility (#2072). Self-service, like
 * WinForms' Options "Default Facility"; the server only accepts a facility the
 * user is mapped to.
 */
export function useSetMyDefaultFacility(userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ facilityId }: { facilityId: number; facilityName?: string }) => {
      await apiClient.put(API.userMeDefaultFacility(facilityId));
      return facilityId;
    },
    onSuccess: (facilityId, { facilityName }) => {
      queryClient.setQueryData(userKeys.myDefaultFacility(userId), facilityId);
      useNotificationStore.getState().addNotification(
        facilityId > 0 && facilityName
          ? `${facilityName} is now your default facility`
          : 'Default facility cleared',
        'success',
      );
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Could not save your default facility', 'error');
    },
  });
}

/**
 * The screen the signed-in user starts on (tblPMSec_User.DF_Start_Form), read
 * from /Me like the default facility. WinForms values: 0 None, 1 Resident Data
 * Grid, 2 Selective Touch, 3-5 Purchasing, 6 Dashboard.
 */
export function useMyStartForm(userId: number) {
  return useQuery({
    queryKey: userKeys.myStartForm(userId),
    queryFn: async () => {
      const { data } = await apiClient.get<{ dF_Start_Form?: number | null }>(API.userMe);
      return Number(data?.dF_Start_Form ?? 0);
    },
    enabled: userId > 0,
    staleTime: 60 * 1000,
  });
}

/** Sets the signed-in user's start screen; self-service, like WinForms' Options "Start Form". */
export function useSetMyStartForm(userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ startForm }: { startForm: number; label?: string }) => {
      await apiClient.put(API.userMeStartForm(startForm));
      return startForm;
    },
    onSuccess: (startForm, { label }) => {
      queryClient.setQueryData(userKeys.myStartForm(userId), startForm);
      // The "/" redirect reads DF_Start_Form from the cached user record.
      queryClient.invalidateQueries({ queryKey: [...userKeys.all, 'detail'] });
      useNotificationStore.getState().addNotification(
        label ? `You will start on ${label} when you sign in` : 'Start screen saved',
        'success',
      );
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Could not save your start screen', 'error');
    },
  });
}

export interface DeleteUserResult {
  success: boolean;
  message: string;
  loginAccountDeleted: boolean;
}

export function useDeleteUser(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, deletedBy }: { userId: number; deletedBy?: string }) => {
      const params = deletedBy ? `?deletedBy=${encodeURIComponent(deletedBy)}` : '';
      const { data } = await apiClient.delete<DeleteUserResult>(`${API.userDelete(facilityId, userId)}${params}`);
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: userKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: userKeys.deleted(facilityId) });
      useNotificationStore.getState().addNotification(result.message, 'success');
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to delete user', 'error');
    },
  });
}

export function useRestoreUser(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      await apiClient.post(API.userRestore(facilityId, userId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: userKeys.deleted(facilityId) });
      useNotificationStore.getState().addNotification('User restored', 'success');
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to restore user', 'error');
    },
  });
}

// ===== KEYCLOAK USER CREATION =====

export interface CreateUserResult {
  success: boolean;
  userId: number;
  keycloakCreated: boolean;
  keycloakExisted: boolean;
  error?: string;
}

export interface CheckKeycloakResult {
  exists: boolean;
  enabled: boolean;
  error?: string;
}

/** Create a new user in both the local database and Keycloak */
export function useCreateUserWithKeycloak(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ user, password }: { user: Partial<User>; password: string }) => {
      const { data } = await apiClient.post<CreateUserResult>(
        API.userCreateWithKeycloak(facilityId),
        { user, password },
      );
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: userKeys.list(facilityId) });
      if (result.keycloakCreated) {
        useNotificationStore.getState().addNotification('User created with Keycloak login', 'success');
      } else if (result.keycloakExisted) {
        useNotificationStore.getState().addNotification('User created (Keycloak account already existed)', 'success');
      } else {
        useNotificationStore.getState().addNotification('User created', 'success');
      }
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to create user', 'error');
    },
  });
}

/** Reset a user's Keycloak password */
export function useResetKeycloakPassword(facilityId: number) {
  return useMutation({
    mutationFn: async ({ email, newPassword, temporary }: { email: string; newPassword: string; temporary: boolean }) => {
      await apiClient.post(API.userResetKeycloakPassword(facilityId), { email, newPassword, temporary });
    },
    onSuccess: () => {
      useNotificationStore.getState().addNotification('Keycloak password reset successful', 'success');
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to reset Keycloak password', 'error');
    },
  });
}

/** Generate a random password via DinoPass */
export async function generatePassword(): Promise<string> {
  const { data } = await apiClient.get<string>('/api/user/GeneratePassword');
  return data;
}

// ===== SIGNATURE IMAGE =====

/** Fetch user signature image as base64 string */
export function useUserSignature(facilityId: number, userId: number) {
  return useQuery({
    queryKey: userKeys.signature(facilityId, userId),
    queryFn: async () => {
      const { data } = await apiClient.get<string | null>(
        API.userSignature(facilityId, userId),
      );
      return data;
    },
    enabled: facilityId > 0 && userId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/** Save user signature image (base64 encoded) */
export function useSaveUserSignature(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, imageBase64 }: { userId: number; imageBase64: string | null }) => {
      await apiClient.post(API.userSignature(facilityId, userId), { imageBase64 });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: userKeys.signature(facilityId, variables.userId),
      });
      // Assessment Sign reads hasSignature from the capabilities (assessmentKeys.all + 'capabilities').
      queryClient.invalidateQueries({ queryKey: ['assessment', 'capabilities'] });
      useNotificationStore.getState().addNotification(
        variables.imageBase64 ? 'Signature saved' : 'Signature removed', 'success');
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to save signature', 'error');
    },
  });
}

// ===== USER HISTORY =====

/**
 * Users > History: what the user did in the facility database plus what was done to the
 * user and their sign-ins (PM_Server), newest first. `range` is null while the page has no
 * valid range; `enabled` is false until the History tab is open.
 */
export function useUserActivity(facilityId: number, userId: number, range: { from: string; to: string } | null, enabled = true) {
  return useQuery<UserActivityResponse>({
    queryKey: userKeys.activity(facilityId, userId, range?.from ?? '', range?.to ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<UserActivityResponse>(
        API.logInteractionUserActivity(facilityId, userId, range!.from, range!.to),
      );
      return data;
    },
    enabled: enabled && facilityId > 0 && userId > 0 && range != null,
    // Keep the grid on screen while another range or time display loads.
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}
